import { useQuery, useMutation, useQueryClient, requestDeals } from '@workspace/api';
import type { PaginatedResponse, PaginationMeta, PaginationLinks } from '@workspace/backoffice/types';
export type { PaginatedResponse, PaginationMeta, PaginationLinks };

export interface Permis {
    type_permis: string;
    date_permis: string;
}

export interface Contact {
    id: number;
    deja_client: boolean;
    numero_client: string | null;
    raison_sociale: string | null;
    type_personne: string;
    civilite: string | null;
    nom: string;
    prenom: string | null;
    date_naissance: string | null;
    adresse_1: string | null;
    adresse_2: string | null;
    adresse_3: string | null;
    code_postal: string | null;
    ville: string | null;
    tel_portable: string | null;
    email: string | null;
    num_permis: string | null;
    permis: Permis[] | null;
    bonus_auto: string | null;
    nb_mois_assurance_auto: number | null;
    bonus_moto: string | null;
    nb_mois_assurance_moto: number | null;
    call_notifications?: CallNotification[];
    // Consentement téléphonique du devis le plus récent (fiche contact uniquement)
    consent_contact?: boolean | null;
    consent_contact_at?: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Coordonnées du conseiller courtier / concessionnaire à l'origine de la demande
 * de rappel sur un devis orange. Le formulaire courtier rend les 4 champs
 * obligatoires, mais l'API reste tolérante (devis orange d'une autre source, ou
 * antérieur à la feature) : chaque champ peut donc être null.
 */
export interface ContactOrange {
    nom: string | null;
    prenom: string | null;
    telephone: string | null;
    email: string | null;
}

/** Un véhicule d'un devis flotte (Livraison). */
export interface Vehicule {
    marque: string | null;
    modele: string | null;
    immatriculation: string | null;
    date_achat: string | null;
    date_mec: string | null;
}

export interface OptionDetail {
    code: string;
    conditions?: string;
}

export type CallDirection = 'inbound' | 'outbound';

export interface CallNotification {
    id: number;
    task_id: number;
    nb_tentatives: number;
    appel_status: number | null;
    appel_status_label: string | null;
    appel_id_conseiller: string;
    conseiller_adm: string | null;
    appel_date: string;
    call_type: number | null;
    call_type_label: string | null;
    call_direction: CallDirection | null;
    appel_duree: number | null;
    out_tel: string | null;
    ani: string | null;
    phone_number: string | null;
    commentaire: string | null;
    task_created_at?: string;
    created_at: string;
}

export interface Quote {
    id: number;
    id_devis: number;
    id_devis_origine: number | null;
    code_produit: string | null;
    etat: string;
    statut: string;

    // Dates
    date_effet: string | null;
    date_echeance_contrat: string | null;
    date_mec: string | null;
    date_achat_vehicule: string | null;

    // Apporteurs et réalisateurs
    id_apporteur_1: number | null;
    nom_apporteur_1: string | null;
    id_apporteur_2: number | null;
    nom_apporteur_2: string | null;
    id_realisateur: number | null;
    nom_realisateur: string | null;
    taux_apporteur_1: string | null;

    // Canal
    canal: string | null;
    marque_blanche: number | null;

    // Véhicule
    immatriculation: string | null;
    vin: string | null;
    type_vehicule: string | null;
    marque_vehicule: string | null;
    modele_vehicule: string | null;
    valeur_vehicule: string | null;
    usage_vehicule: number | null;

    // Formule et options
    acte_gestion: string | null;
    motif_avenant: string | null;
    formule_choisie: string | null;
    nombre_vehicules: number | null;
    vehicules: Vehicule[] | null;
    options_details: OptionDetail[] | null;
    fractionnement: string | null;
    type_mer: string | null;

    // Tarification
    prime_totale_annuelle_frac_annuel: string | null;
    prime_totale_annuelle_frac_choisi: string | null;
    montant_comptant: string | null;
    honoraires: string | null;
    frais_recurrents: string | null;

    // Promotion
    code_promo: string | null;
    montant_comptant_promo: string | null;
    montant_reduc_promo: string | null;

    // Antécédents et risques
    resiliation_assureur: boolean;
    motif_resiliation_assureur: string | null;
    annulation_permis: boolean;
    suspension_permis: boolean;
    nb_mois_suspension_permis: number;
    condamnation_alcoolemie: boolean;
    condamnation_stupefiants: boolean;
    condamnation_deli_fuite: boolean;
    condamnation_conduite_sans_assurance: boolean;
    nb_sinistres: number;
    consent_contact: boolean;
    contact_orange: ContactOrange | null;

    // Informations du conducteur (si différent du souscripteur)
    conducteur_nom: string | null;
    conducteur_prenom: string | null;
    conducteur_date_naissance: string | null;
    conducteur_num_permis: string | null;
    conducteur_permis: Permis[] | null;
    conducteur_bonus_auto: string | null;
    conducteur_nb_mois_assurance_auto: number | null;
    conducteur_bonus_moto: string | null;
    conducteur_nb_mois_assurance_moto: number | null;
    conducteur_nb_sinistres: number | null;

    // Historique assurance
    compagnie_actuelle: string | null;

    // Paiement et contrat
    mode_paiement_comptant: string | null;
    mode_paiement_terme: string | null;
    numero_police: string | null;

    // URLs documents
    url_lien_devis: string | null;
    url_lien_fiche_conseil: string | null;
    url_lien_contrat: string | null;
    url_lien_hamon: string | null;
    url_lien_mandat_sepa: string | null;
    url_lien_attestation: string | null;
    url_lien_dispositions_generales: string | null;
    url_lien_convention_assistance: string | null;
    url_lien_document_information: string | null;

    // URLs de reprise
    url_reprise: string | null;
    url_souscription: string | null;

    // Feu tricolore
    feu_tricolore: string | null;
    message_feu_tricolore: string | null;

    // Permissions
    can_view_details: boolean;
    can_make_actions: boolean;

    call_notifications?: CallNotification[];
    contact: Contact;
    created_at: string;
    updated_at: string;
}


export interface QuoteFilters {
    search?: string;
    statut?: string | string[];
    etat?: string | string[];
    code_produit?: string | string[];
    canal?: string | string[];
    acte_gestion?: string | string[];
    type_mer?: string | string[];
    id_apporteur_1?: number;
    nom_apporteur_1?: string;
    id_apporteur_2?: number;
    nom_apporteur_2?: string;
    id_realisateur?: number;
    nom_realisateur?: string;
    sans_realisateur?: boolean;
    feu_tricolore?: string;
    created_after?: string;
    created_before?: string;
    per_page?: number;
    page?: number;
    [key: string]: string | number | string[] | boolean | undefined;
}

/**
 * Hook pour récupérer la liste des devis avec filtres et pagination
 */
export function useQuotes(filters: QuoteFilters = {}, options: { enabled?: boolean } = {}) {
    return useQuery<PaginatedResponse<Quote>>({
        queryKey: ['deals', 'quotes', filters],
        queryFn: async () => {
            const response = await requestDeals.get<PaginatedResponse<Quote>>('/api/quotes', { params: filters });
            return response.data;
        },
        staleTime: 30 * 1000, // 30 secondes
        enabled: options.enabled ?? true,
    });
}

/**
 * Hook pour récupérer un devis unique par son ID
 */
export function useQuote(id: string | number) {
    return useQuery<{ data: Quote }>({
        queryKey: ['deals', 'quotes', id],
        queryFn: async () => {
            const response = await requestDeals.get<{ data: Quote }>(`/api/quotes/${id}`);
            return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        enabled: !!id,
    });
}

/**
 * Hook pour récupérer les devis associés (toute la chaîne de transformation via id_devis_origine).
 * Utilise l'endpoint dédié qui parcourt la chaîne complète côté backend.
 */
export function useRelatedQuotes(currentQuote: Quote | undefined) {
    return useQuery<Quote[]>({
        queryKey: ['deals', 'quotes', 'related', currentQuote?.id],
        queryFn: async () => {
            if (!currentQuote) return [];

            const response = await requestDeals.get<{ data: Quote[] }>(`/api/quotes/${currentQuote.id}/related`);

            return response.data.data;
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!currentQuote,
    });
}

/**
 * Hook pour modifier le statut d'un devis
 */
export function useUpdateQuoteStatus() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, statut }: { id: number; statut: string }) => {
            const response = await requestDeals.patch(`/api/quotes/${id}/status`, { statut });
            return response.data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['deals', 'quotes'] });
            queryClient.invalidateQueries({ queryKey: ['deals', 'quotes', variables.id] });
        },
    });
}

/**
 * Hook pour attribuer un devis à un réalisateur (gestionnaire ADM / télévente)
 */
export function useAssignRealisateur() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, id_realisateur }: { id: number; id_realisateur: number }) => {
            const response = await requestDeals.patch(`/api/quotes/${id}/realisateur`, { id_realisateur });
            return response.data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['deals', 'quotes'] });
            queryClient.invalidateQueries({ queryKey: ['deals', 'quotes', variables.id] });
            queryClient.invalidateQueries({ queryKey: ['deals', 'stats'] });
        },
    });
}

/**
 * Hook pour supprimer un devis
 */
export function useDeleteQuote() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            const response = await requestDeals.delete(`/api/quotes/${id}`);
            return response.data;
        },
        onSuccess: (_, id) => {
            // Retirer immédiatement le devis du cache
            queryClient.removeQueries({ queryKey: ['deals', 'quotes', id] });
            // Invalider toutes les listes de devis
            queryClient.invalidateQueries({ queryKey: ['deals', 'quotes'] });
            // Invalider les stats
            queryClient.invalidateQueries({ queryKey: ['deals', 'stats'] });
        },
    });
}
